/* Leaft Blade V2: the "Jordan" landing page buy box.
   One interactive system on the page. State is pack and frequency; every
   derived string recomputes on change and the closing CTA's summary line
   follows because it lives in the same section. Prices and selling plan ids
   are read from the cells the section rendered from the catalogue; nothing
   here is hard coded. Add to cart posts the real selling plan and then, per
   the section setting, goes to checkout or the cart. */

(() => {
  if (customElements.get('lb2-jordan-buy')) return;

  class LB2JordanBuy extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-lb2-jordan]') || document;
      this.tiers = [...this.querySelectorAll('[data-jo-tier]')];
      this.freqs = [...this.querySelectorAll('[data-jo-freq]')];
      this.form = this.querySelector('[data-jo-form]');
      this.variantInput = this.querySelector('[data-jo-variant]');
      this.planInput = this.querySelector('[data-jo-plan]');
      this.addBtn = this.querySelector('[data-jo-add]');
      this.msg = this.querySelector('[data-jo-msg]');
      this.planOpts = [...this.querySelectorAll('[data-jo-plan-opt]')];
      this.chooserBtn = this.querySelector('[data-jo-chooser]');
      this.chooserPanel = this.querySelector('[data-jo-chooser-panel]');
      this.freqField = this.querySelector('[data-jo-freq-field]');
      this.sticky = this.root.querySelector ? this.root.querySelector('[data-jo-sticky]') : null;
      this.stickyAdd = this.sticky ? this.sticky.querySelector('[data-jo-sticky-add]') : null;
      this.added = false;
      this.plan = 'sub';
      this.copy = this.dataset;

      if (this.chooserBtn) {
        this.chooserBtn.addEventListener('click', () => this.toggleChooser());
      }
      if (this.stickyAdd) {
        // One add to cart for both surfaces: the bar submits the card's form.
        this.stickyAdd.addEventListener('click', () => this.form?.requestSubmit());
      }
      if (this.sticky) this.watchSticky();

      this.addEventListener('click', (e) => {
        const tier = e.target.closest('[data-jo-tier]');
        const freq = e.target.closest('[data-jo-freq]');
        const planOpt = e.target.closest('[data-jo-plan-opt]');
        if (tier) this.select(this.tiers, tier);
        if (freq) this.select(this.freqs, freq);
        if (planOpt && !planOpt.disabled) {
          this.select(this.planOpts, planOpt);
          this.plan = planOpt.dataset.joPlanOpt === 'once' ? 'once' : 'sub';
          this.planBeforeLock = null;
        }
        if (tier || freq || planOpt) {
          this.added = false;
          this.update();
        }
      });
      this.addEventListener('keydown', (e) => this.arrowKeys(e));
      if (this.form) this.form.addEventListener('submit', (e) => this.submit(e));

      this.update();
    }

    setPlan(plan) {
      this.plan = plan === 'once' ? 'once' : 'sub';
      const opt = this.planOpts.find((o) => o.dataset.joPlanOpt === this.plan);
      if (opt) this.select(this.planOpts, opt);
    }

    select(group, target) {
      group.forEach((el) => el.setAttribute('aria-checked', el === target ? 'true' : 'false'));
    }

    // Radiogroup semantics: arrow keys move the selection within a group.
    arrowKeys(e) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      const cell = e.target.closest('[data-jo-tier], [data-jo-freq]');
      if (!cell) return;
      const group = cell.hasAttribute('data-jo-tier') ? this.tiers : this.freqs;
      const visible = group.filter((el) => !el.hidden);
      const i = visible.indexOf(cell);
      const delta = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1;
      const next = visible[(i + delta + visible.length) % visible.length];
      e.preventDefault();
      this.select(group, next);
      next.focus();
      this.added = false;
      this.update();
    }

    get tier() {
      return this.tiers.find((t) => t.getAttribute('aria-checked') === 'true') || this.tiers[0];
    }

    get freq() {
      return this.freqs.find((f) => f.getAttribute('aria-checked') === 'true' && !f.hidden)
        || this.freqs.find((f) => !f.hidden) || this.freqs[0];
    }

    setText(selector, text) {
      this.root.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
        if (text === '') el.hidden = true;
        else el.hidden = false;
      });
    }

    money(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    // How a box of `blades` lands at an interval of `weeks`, stated honestly:
    // 28 every 4 weeks is exactly one a day; 7 every fortnight is 3 to 4 a week.
    cadence(blades, weeks) {
      const perWeek = blades / weeks;
      const perDay = perWeek / 7;
      if (Number.isInteger(perDay)) return perDay === 1 ? 'One a day' : `${perDay} a day`;
      if (perDay > 1) return `${Math.floor(perDay)} to ${Math.ceil(perDay)} a day`;
      if (Number.isInteger(perWeek)) return `${perWeek} a week`;
      return `${Math.floor(perWeek)} to ${Math.ceil(perWeek)} a week`;
    }

    // Only frequencies the selected box has a selling plan for are offered;
    // a product with no plans at all keeps every cell. Each hidden cell is
    // logged once so drift between theme and catalogue is visible.
    applyAvailability(tier) {
      let plans = {};
      try {
        plans = JSON.parse(tier.dataset.plans || '{}');
      } catch {
        plans = {};
      }
      const product = tier.dataset.title || `the ${tier.dataset.blades}-Blade box`;
      const hasPlans = Object.keys(plans).length > 0;
      this.warned = this.warned || new Set();
      if (!hasPlans && !this.warned.has(`none:${product}`)) {
        this.warned.add(`none:${product}`);
        console.warn(`[lb2-jordan] ${product} has no selling plans; the add to cart cannot attach a subscription.`);
      }
      this.freqs.forEach((f) => {
        const available = !hasPlans || Object.prototype.hasOwnProperty.call(plans, f.dataset.weeks);
        f.hidden = !available;
        if (!available) {
          const key = `${f.dataset.weeks}:${product}`;
          if (!this.warned.has(key)) {
            this.warned.add(key);
            const label = f.querySelector('.lb2-jo__cell-label')?.textContent || `${f.dataset.weeks} weeks`;
            console.warn(`[lb2-jordan] Frequency "${label}" (${f.dataset.weeks} weeks) hidden: ${product} has no matching selling plan.`);
          }
        }
      });
      const current = this.freqs.find((f) => f.getAttribute('aria-checked') === 'true');
      if (!current || current.hidden) {
        const first = this.freqs.find((f) => !f.hidden);
        if (first) this.select(this.freqs, first);
      }
    }

    planFor(tier, weeks) {
      try {
        return JSON.parse(tier.dataset.plans || '{}')[weeks] || '';
      } catch {
        return '';
      }
    }

    // The chooser is a disclosure: the summary says what is selected, the
    // panel holds the size and the frequency.
    toggleChooser(force) {
      if (!this.chooserPanel || !this.chooserBtn) return;
      const open = force === undefined ? this.chooserPanel.hidden : force;
      this.chooserPanel.hidden = !open;
      this.chooserBtn.setAttribute('aria-expanded', String(open));
      const sign = this.querySelector('[data-jo-chooser-sign]');
      if (sign) sign.textContent = open ? '\u2013' : '+';
    }

    // The sticky bar appears once the card has scrolled out of view, so it
    // never competes with the button it mirrors.
    watchSticky() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        ([entry]) => {
          this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        },
        { rootMargin: '0px 0px -40% 0px' }
      );
      io.observe(this);
    }

    update() {
      const tier = this.tier;
      if (!tier) return;
      this.applyAvailability(tier);
      const freq = this.freq;
      if (!freq) return;
      const pack = parseInt(tier.dataset.blades, 10);
      const subCents = parseInt(tier.dataset.cents, 10);
      const onceCents = parseInt(tier.dataset.onceCents, 10) || 0;
      const firstCents = parseInt(tier.dataset.firstCents, 10) || 0;
      const weeks = freq.dataset.weeks;
      const word = freq.dataset.word;
      const adj = freq.dataset.adj;
      // A size sold once only forces the purchase option. The choice the
      // shopper made is remembered, so moving back to a subscribable size
      // returns them to Subscribe rather than stranding them on Buy once.
      const onceOnly = tier.dataset.oneTime === 'true';
      if (onceOnly && this.plan !== 'once') {
        this.planBeforeLock = this.plan;
        this.setPlan('once');
      } else if (!onceOnly && this.planBeforeLock) {
        this.setPlan(this.planBeforeLock);
        this.planBeforeLock = null;
      }
      const sub = this.plan !== 'once';
      // The subscribe row stays visible on a one-off size, but says so and
      // cannot be chosen: hiding it would make the page look broken.
      this.planOpts.forEach((o) => {
        if (o.dataset.joPlanOpt !== 'sub') return;
        o.disabled = onceOnly;
        o.setAttribute('aria-disabled', String(onceOnly));
      });
      this.setText('[data-jo-sub-note-off]', onceOnly ? this.copy.subOffNote || '' : '');

      // Subscribing to a box with a first box offer is charged the discounted
      // price today, so that is the figure on every button and in the cart.
      const cents = sub ? (firstCents || subCents) : onceCents || subCents;
      const per = this.money(Math.round((sub ? subCents : onceCents || subCents) / pack));
      const total = this.money(cents);

      const orderLine = sub ? `${pack} Blades, ${word}` : `${pack} Blades, one delivery`;
      let perLine;
      if (!sub) {
        perLine = `${per} a Blade`;
      } else if (firstCents) {
        perLine = `First box ${this.money(firstCents)}, then ${this.money(subCents)} · skip, pause or cancel anytime`;
      } else {
        perLine = `${per} a Blade · skip, pause or cancel anytime`;
      }

      // Each cell carries its own rate from the theme's shipping settings: the
      // NZ rate can step up at a threshold (flat since 20 Oct 2026) and the
      // 28 box is free. A first box is
      // rated on what it charges, which can fall under the threshold.
      let shipRate = sub ? tier.dataset.ship : tier.dataset.onceShip;
      if (sub && firstCents && tier.dataset.firstShip) shipRate = tier.dataset.firstShip;
      shipRate = shipRate || '';
      const shipLine = shipRate === 'free' ? 'Shipping free on this box' : `Shipping ${shipRate} on this box`;
      const ctaLabel = sub ? `Start the ${adj} box · ${pack} Blades` : `Add one box · ${pack} Blades`;
      const cta = this.added ? `Added · ${pack} Blades` : ctaLabel;

      // The plan rows: both prices, both per Blade figures, and the badge.
      this.setText('[data-jo-sub-price]', onceOnly ? '' : this.money(firstCents || subCents));
      this.setText('[data-jo-once-price]', onceCents ? this.money(onceCents) : '');
      this.setText('[data-jo-sub-per]', onceOnly ? '' : `${this.money(Math.round(subCents / pack))} a Blade`);
      this.setText('[data-jo-once-per]', onceCents ? `${this.money(Math.round(onceCents / pack))} a Blade` : '');
      const subNote = onceOnly
        ? this.copy.subOffNote || 'Not available on this size'
        : firstCents
          ? (this.copy.firstNote || 'Then {price} {cadence}').replace('{price}', this.money(subCents)).replace('{cadence}', word)
          : `${this.copy.subNote || 'Delivered'} ${word}`;
      this.setText('[data-jo-sub-note]', subNote);
      const badge = this.querySelector('[data-jo-plan-badge]');
      if (badge) {
        let text = '';
        if (firstCents) {
          text = this.copy.firstBadge || 'First box half price';
        } else if (onceCents > subCents) {
          const pct = Math.round((1 - subCents / onceCents) * 100);
          if (pct > 0) text = (this.copy.saveBadge || 'Save {n}%').replace('{n}', String(pct));
        }
        badge.textContent = text;
        badge.hidden = text === '';
      }

      // The second trust tick depends on the plan.
      this.setText('[data-jo-tick-plan]', sub ? this.copy.tickSub || '' : this.copy.tickOnce || '');
      if (this.freqField) this.freqField.hidden = !sub;
      // What the box holds, so the card says what is being bought.
      this.setText('[data-jo-pack]', (this.copy.packLine || '{n} Blades, {size} each')
        .replace('{n}', String(pack))
        .replace('{size}', this.copy.serveSize || '100 ml'));

      // The cells are too narrow for a note on a phone, so the selected box's
      // note is repeated on its own line under the row.
      this.setText('[data-jo-size-note]', tier.dataset.note ? `${pack} Blades · ${tier.dataset.note}` : '');

      const summary = sub ? `${pack} Blades · ${word}` : `${pack} Blades · buy once`;
      this.setText('[data-jo-summary]', summary);
      const stickySub = (sub ? `${pack} Blades · subscribe, ${word}` : `${pack} Blades · buy once`)
        + (shipRate === 'free' ? ' · free ship' : '');
      this.setText('[data-jo-sticky-sub]', stickySub);

      this.root.querySelectorAll('[data-jo-order]').forEach((el) => (el.textContent = orderLine));
      this.root.querySelectorAll('[data-jo-per]').forEach((el) => (el.textContent = perLine));
      this.root.querySelectorAll('[data-jo-total]').forEach((el) => (el.textContent = total));
      this.root.querySelectorAll('[data-jo-ship]').forEach((el) => (el.textContent = shipLine));
      this.root.querySelectorAll('[data-jo-cta]').forEach((el) => (el.textContent = cta));
      if (this.stickyAdd) this.stickyAdd.textContent = this.added ? this.copy.addedLabel || 'Added' : this.copy.stickyAdd || 'Add to cart';

      // Frequency notes without an editorial override state how the chosen box
      // lands at each cadence, the same maths as the product page.
      this.freqs.forEach((f) => {
        const note = f.querySelector('[data-jo-dyn-note]');
        if (note) note.textContent = this.cadence(pack, parseInt(f.dataset.weeks, 10) || 1);
      });

      if (this.variantInput) {
        this.variantInput.value = sub ? tier.dataset.variant : tier.dataset.onceVariant || tier.dataset.variant;
      }
      if (this.planInput) {
        const plan = sub ? this.planFor(tier, weeks) : '';
        this.planInput.value = plan;
        this.planInput.disabled = plan === '';
      }
      if (this.addBtn) this.addBtn.dataset.added = String(this.added);
    }

    async submit(e) {
      e.preventDefault();
      if (!this.form || !this.addBtn) return;
      const item = { id: Number(this.variantInput.value), quantity: 1 };
      if (this.planInput && !this.planInput.disabled && this.planInput.value) {
        item.selling_plan = Number(this.planInput.value);
      }
      this.addBtn.disabled = true;
      if (this.msg) this.msg.textContent = '';
      try {
        const res = await fetch(this.form.action + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add to cart.');
        }
        this.added = true;
        this.update();
        const dest = (this.root.dataset && this.root.dataset.postAdd) === 'cart' ? '/cart' : '/checkout';
        window.location.assign(dest);
      } catch (err) {
        this.addBtn.disabled = false;
        if (this.msg) this.msg.textContent = err.message || 'Something went wrong. Please try again.';
      }
    }
  }

  customElements.define('lb2-jordan-buy', LB2JordanBuy);
})();
